/**
 * Who may take over a company record we created, and on what evidence.
 *
 * About 170 rows in `companies` were made by the enrichment pipeline so that a
 * scraped vacancy could carry a logo. Nobody at those employers has an account,
 * and the day one of them registers, the right outcome is that they manage the
 * record we already have — "one employer is one record" (PRODUCT.md) — rather
 * than a second company under the same name with none of the history.
 *
 * Being handed a company is being handed its vacancies and its applicants, so
 * the question "is this person really at that employer" has exactly three
 * honest answers, and this module is where they are decided:
 *
 *  - **The address proves it.** The account's email is at the domain on record
 *    and the account has confirmed that it owns the address. The claim goes
 *    through with nobody involved.
 *  - **Only a person can tell.** There is no usable domain on record, or the
 *    address is at a different corporate domain — a subdomain of the one on
 *    record included (`domainBelongsTo` says why). The claim goes to the team
 *    with the evidence laid out, and somebody decides.
 *  - **The address proves nothing.** The record has a real domain and the
 *    account is a free mailbox. Refused, with a sentence saying what would work.
 *
 * Pure and dependency-free on purpose: the backend enforces it
 * (`functions/claim-company.ts`), and the registration form runs the same
 * function to tell the person *before* they create an account which of the
 * three they are in. The form's answer is a courtesy; the server's is the one
 * that counts.
 */

import { isFreeMailDomain } from "./employer_identity";
import { PUBLIC_SITE_URL } from "./public_site";

/**
 * A domain as the enrichment might have stored it, reduced to a bare host.
 *
 * Every stored value sampled on 2026-09-21 was already bare, but the column is
 * typed by hand in the panel too, and "https://www.Acme.com/" is what a person
 * pastes.
 */
export function normalizeDomain(raw: string | null | undefined): string {
    let value = (raw ?? "").trim().toLowerCase();
    value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
    value = value.split(/[/?#]/)[0] ?? "";
    value = value.replace(/^www\./, "").replace(/\.+$/, "");
    return value;
}

/** The domain half of an address, normalised the same way. */
export function emailDomainOf(address: string | null | undefined): string {
    const value = (address ?? "").trim().toLowerCase();
    const at = value.lastIndexOf("@");
    return at === -1 ? "" : normalizeDomain(value.slice(at + 1));
}

/**
 * First labels of a two-label domain that make it a registry, not an employer.
 *
 * The enrichment stored `gob.mx` and `edu.mx` as company domains (both present
 * on 2026-09-21). Matching under either would hand a record called "Gobierno
 * de México" to anybody writing from any ministry, and a university's record to
 * anybody at any university in the country.
 */
const REGISTRY_LABELS = new Set(["com", "org", "net", "gob", "gov", "edu", "ac", "co", "nom", "mil"]);

/**
 * Whether a company's stored domain can serve as proof at all.
 *
 * Not when it is empty, not when it is a free mailbox provider (the brand
 * enricher takes whatever a site declares, and a `gmail.com` company is a
 * matter of time), and not when it is a bare registry suffix.
 */
export function isUsableCompanyDomain(raw: string | null | undefined): boolean {
    const domain = normalizeDomain(raw);
    if (!domain || !domain.includes(".")) return false;
    if (isFreeMailDomain(domain)) return false;
    const labels = domain.split(".");
    if (labels.length === 2 && REGISTRY_LABELS.has(labels[0])) return false;
    return true;
}

/**
 * The address is at the company's domain: exactly the same host, nothing under it.
 *
 * A host *under* the company's domain used to count, on the reasoning that
 * `rrhh.acciona.com` is Acciona's. It usually is — but the case that decides
 * the rule is the other one: a university's record stored as `ipn.mx` would
 * then be joined, with nobody looking, by anybody with an `@alumno.ipn.mx`
 * student mailbox, and every large institution hands out addresses under a
 * subdomain to people who do not speak for it (students, alumni, a hosted
 * spin-off). Joining a company is being handed its vacancies and applicants,
 * so a subdomain is not proof; it is evidence, and evidence goes to a person.
 * `claimPathFor` therefore sends it to review as a `domain_mismatch` — the
 * team sees "su correo es de alumno.ipn.mx y la ficha dice ipn.mx" and
 * decides — rather than refusing it: the HR department on a subdomain is a
 * real employer we want, it just waits for a yes.
 *
 * The reverse was never a match either — an address at `acciona.com` says
 * nothing about a record stored as `energia.acciona.com`, which could be a
 * separately run business — and neither is a lookalike: `acciona.com.evil.io`
 * ends in something else entirely.
 */
export function domainBelongsTo(emailDomain: string, companyDomain: string): boolean {
    const email = normalizeDomain(emailDomain);
    const company = normalizeDomain(companyDomain);
    if (!email || !company) return false;
    return email === company;
}

export type ClaimPath =
    /** Address at the domain, address confirmed: joins now. */
    | { path: "join" }
    /** Address at the domain, not yet confirmed: joins once it is. */
    | { path: "verify" }
    /** A person decides. */
    | { path: "review"; reason: "no_domain" | "domain_mismatch"; freeMail: boolean }
    /** A free mailbox against a record that has a real domain. */
    | { path: "refuse"; reason: "free_mail" };

/**
 * Which of the three a claim is.
 *
 * `emailVerified` is not optional, and not decoration. Registering as
 * `ceo@acciona.com` costs nothing and proves nothing — anyone can type it — so
 * an unconfirmed address at the right domain is a claim waiting on one click in
 * that mailbox, never a membership.
 */
export function claimPathFor(input: {
    email: string | null | undefined;
    emailVerified: boolean;
    companyDomain: string | null | undefined;
}): ClaimPath {
    const emailDomain = emailDomainOf(input.email);
    const freeMail = !emailDomain || isFreeMailDomain(emailDomain);

    if (!isUsableCompanyDomain(input.companyDomain)) {
        return { path: "review", reason: "no_domain", freeMail };
    }
    if (freeMail) return { path: "refuse", reason: "free_mail" };
    if (!domainBelongsTo(emailDomain, input.companyDomain ?? "")) {
        return { path: "review", reason: "domain_mismatch", freeMail: false };
    }
    return input.emailVerified ? { path: "join" } : { path: "verify" };
}

/**
 * What the `empresa` parameter carries: the slug when the record has one.
 *
 * No company row had a slug on 2026-09-21 — the column exists and nothing fills
 * it — so the id stands in, and `claim-company` resolves either. A link built
 * today keeps working the day slugs arrive, because the id still resolves.
 */
export function claimParam(company: { id: string; slug?: string | null }): string {
    const slug = (company.slug ?? "").trim();
    return slug || String(company.id);
}

/** `/registro/empresa?empresa=…`, relative, for links on the site itself. */
export function claimPath(company: { id: string; slug?: string | null }): string {
    return `/registro/empresa?empresa=${encodeURIComponent(claimParam(company))}`;
}

/** The same, absolute, for anything that leaves the site: an email, the panel. */
export function claimUrl(company: { id: string; slug?: string | null }): string {
    return `${PUBLIC_SITE_URL}${claimPath(company)}`;
}

/**
 * Whether a company can be claimed through the link at all.
 *
 * `is_claimed` is the flag the board reads; a company with members is claimed
 * whatever the flag says. Either one closes the door — a second person at an
 * employer that already has an account is a colleague to invite, not a claim.
 */
export function isClaimable(company: { is_claimed?: boolean | null }, hasMembers = false): boolean {
    return company.is_claimed !== true && !hasMembers;
}

/** The placeholder a template writes for the claim link. See `fillTemplate`. */
export const CLAIM_LINK_PLACEHOLDER = "{{enlace_ficha}}";
